'use client';
import React from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { X, HelpCircle } from 'lucide-react';

interface Props {
  currentStep: number;
  totalSteps: number;
  onGoTo: (step: number) => void;
}

const stepLabels = [
  'Welcome', 'Visa Type', 'Your Info', 'Requirements',
  'Documents', 'Validation', 'AI Generation', 'Download', 'Payment',
];

export default function WizardTopBar({ currentStep, totalSteps, onGoTo }: Props) {
  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-gray-100 shadow-sm">
      <div className="max-w-screen-xl mx-auto px-4 lg:px-8 h-14 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link href="/landing-page" className="flex items-center gap-2 shrink-0">
          <AppLogo size={28} />
          <span className="font-bold text-sm text-gray-900 hidden sm:block">VisaPilot AI</span>
        </Link>

        {/* Step pills (desktop) */}
        <div className="hidden lg:flex items-center gap-1 overflow-x-auto">
          {stepLabels.map((label, idx) => {
            const stepNum = idx + 1;
            const isActive = stepNum === currentStep;
            const isDone = stepNum < currentStep;
            return (
              <button
                key={`wizard-step-pill-${stepNum}`}
                onClick={() => isDone && onGoTo(stepNum)}
                disabled={!isDone && stepNum !== currentStep}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all duration-150 whitespace-nowrap ${
                  isActive
                    ? 'bg-blue-600 text-white'
                    : isDone
                    ? 'text-green-700 bg-green-50 hover:bg-green-100 cursor-pointer' :'text-gray-400 cursor-default'
                }`}
              >
                <span className={`w-4 h-4 rounded-full flex items-center justify-center text-xs shrink-0 ${
                  isActive ? 'bg-white/20' : isDone ? 'bg-green-200' : 'bg-gray-100'
                }`}>
                  {isDone ? '✓' : stepNum}
                </span>
                {label}
              </button>
            );
          })}
        </div>

        {/* Mobile: step indicator */}
        <div className="lg:hidden text-sm font-semibold text-gray-600">
          Step {currentStep} of {totalSteps} — {stepLabels[currentStep - 1]}
        </div>

        {/* Right actions */}
        <div className="flex items-center gap-2 shrink-0">
          <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors" aria-label="Help">
            <HelpCircle size={17} />
          </button>
          <Link href="/landing-page" className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors" aria-label="Exit wizard">
            <X size={17} />
          </Link>
        </div>
      </div>
    </header>
  );
}